import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useSuccessionTheme } from '../SuccessionThemeProvider';
import { useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import numeral from 'numeral';
import PartialSaleEditForm from './PartialSaleEditForm';
import { currencySymbol } from '../../../utils/formatters';
import { openSuccessionDrawer } from '../../../actions/successionDrawerActionCreators';
import SingleColumnTemplate from '../../../../../ui/layouts/SingleColumnTemplate';
import Button from '../../../../../ui/canon/Button';
import Modal from '../../../../../ui/canon/Modal';

const EXPRESS_INTEREST_FOR_PARTIAL_SALE = gql`
  mutation expressInterestForPartialSale($partialSaleId: ID!, $requestMessage: String!) {
    expressInterestForPartialSale(partialSaleId: $partialSaleId, requestMessage: $requestMessage) {
      id
      segment_details
    }
  }
`;

const TOGGLE_PARTIAL_SALE_STATUS = gql`
  mutation TogglePartialSaleStatus($id: ID!, $sale_status: String!) {
    toggleStatusForPartialSale(id: $id, sale_status: $sale_status) {
      id
      sale_status
      reason_for_close
      listed_at
    }
  }
`;

const UPDATE_PARTIAL_SALE_INTEREST_STATUS = gql`
  mutation UpdatePartialSaleInterestStatus($partialSaleListingUserId: ID!, $status: String!) {
    updatePartialSaleInterestStatus(partialSaleListingUserId: $partialSaleListingUserId, status: $status) {
      id
      status
      buyer {
        id
        name
        allowed_name
      }
    }
  }
`;

const REMOVE_PARTIAL_SALE_LISTING = gql`
  mutation RemovePartialSaleListing($id: ID!, $userRole: String!) {
    removePartialSaleListing(id: $id, userRole: $userRole) {
      id
    }
  }
`;



const SuccessionPartialSalesMarketplace = ({ 
  partialSales, 
  user, 
  company, 
  partialSaleQuestions, 
  features,
  isModernizationEnabled,
  pageTitle,
  isMyPartialSales 
}) => {
  const intl = useIntl();
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();
  const dispatch = useDispatch();

  // Add CSS styles for 3D flip animation and primary pulse
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .transform-style-preserve-3d {
        transform-style: preserve-3d;
      }
      .backface-hidden {
        backface-visibility: hidden;
      }
      .rotate-y-180 {
        transform: rotateY(180deg);
      }
      @keyframes pulse-primary {
        0%, 100% {
          box-shadow: 0 0 0 2px var(--succession-primary), 0 0 10px var(--succession-primary), 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        50% {
          box-shadow: 0 0 0 2px var(--succession-primary), 0 0 25px var(--succession-primary), 0 0 40px var(--succession-primary), 0 4px 12px rgba(0, 0, 0, 0.1);
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Use local state for listings so we can update them immediately
  const [localPartialSales, setLocalPartialSales] = useState(partialSales || []);
  
  // Update local state when prop changes
  React.useEffect(() => {
    setLocalPartialSales(partialSales || []);
  }, [partialSales]);

  const [expressInterest] = useMutation(EXPRESS_INTEREST_FOR_PARTIAL_SALE);
  const [toggleStatus] = useMutation(TOGGLE_PARTIAL_SALE_STATUS);
  const [updateInterestStatus] = useMutation(UPDATE_PARTIAL_SALE_INTEREST_STATUS);
  const [removeListing] = useMutation(REMOVE_PARTIAL_SALE_LISTING);
  const [selectedListing, setSelectedListing] = useState(null);
  const [isInterestModalOpen, setIsInterestModalOpen] = useState(false);
  const [interestMessage, setInterestMessage] = useState('');
  const [isSubmittingInterest, setIsSubmittingInterest] = useState(false);
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [editingListings, setEditingListings] = useState(new Map());
  const [cardHeight, setCardHeight] = useState(600); // Default to 37.5rem (600px)
  const [heightCalculated, setHeightCalculated] = useState(false);
  
  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success', 'error', 'info'
  
  // Remove dialog state
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [listingToRemove, setListingToRemove] = useState(null);

  const partialSalesPropertyCasualty = company?.feature_types?.includes('partial_sales_property_casualty') || false;
  const hideProductSalesPurchasePrice = company?.feature_types?.includes('hide_partial_sales_purchase_price') || false;
  const partialSalesDetailedQuestions = company?.feature_types?.includes('partial_sales_detailed_questions') || false;

  // Toast notification function
  const showToastNotification = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    
    // Auto-hide after 4 seconds
    setTimeout(() => {
      setShowToast(false);
    }, 4000);
  };

  const handleExpressInterest = (listing) => {
    setSelectedListing(listing);
    setInterestMessage(`Hi, I'm interested in learning more about your ${listing.segment_details || 'practice segment'}. Could we schedule a time to discuss the details?`);
    setIsInterestModalOpen(true);
  };

  const handlePublishListing = async (listing) => {
    try {
      const result = await toggleStatus({
        variables: {
          id: listing.id,
          sale_status: 'open'
        }
      });

      if (result.data?.toggleStatusForPartialSale) {
        // Update local state
        setLocalPartialSales(prev => 
          prev.map(item => 
            item.id === listing.id 
              ? { ...item, sale_status: 'open', listed_at: result.data.toggleStatusForPartialSale.listed_at }
              : item
          )
        );
      }
    } catch (error) {
      console.error('Error publishing listing:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleSubmitInterest = async () => {
    if (!selectedListing || !interestMessage.trim()) return;

    setIsSubmittingInterest(true);
    try {
      await expressInterest({
        variables: {
          partialSaleId: selectedListing.id,
          requestMessage: interestMessage.trim()
        }
      });
      
      setIsInterestModalOpen(false);
      setSelectedListing(null);
      setInterestMessage('');
      
      // Update local state to reflect that user has now expressed interest
      setLocalPartialSales(prev => 
        prev.map(listing => 
          listing.id === selectedListing.id 
            ? { 
                ...listing, 
                current_user_is_an_interest: true,
                interests_count: (listing.interests_count || 0) + 1
              }
            : listing
        )
      );
      
      // Show success message
      showToastNotification(intl.formatMessage({ id: 'succession.partial_sales.marketplace.toast_interest_success' }), 'success');
    } catch (error) {
      console.error('Error submitting interest:', error);

      // Check if it's a duplicate interest error
      if (error.message && error.message.includes('already expressed interest')) {
        showToastNotification(intl.formatMessage({ id: 'succession.partial_sales.marketplace.toast_interest_duplicate' }), 'error');
      } else {
        showToastNotification(intl.formatMessage({ id: 'succession.partial_sales.marketplace.toast_interest_failed' }), 'error');
      }
      
      // Update local state to reflect that user has already expressed interest
      if (error.message && error.message.includes('already expressed interest')) {
        setLocalPartialSales(prev => 
          prev.map(listing => 
            listing.id === selectedListing.id 
              ? { ...listing, current_user_is_an_interest: true }
              : listing
          )
        );
      }
    } finally {
      setIsSubmittingInterest(false);
    }
  };

  const handleApproveInterest = async (listingUserId, listingId) => {
    try {
      const result = await updateInterestStatus({
        variables: {
          partialSaleListingUserId: listingUserId,
          status: 'interested'
        }
      });

      if (result.data?.updatePartialSaleInterestStatus) {
        console.log('Approve mutation result:', result.data.updatePartialSaleInterestStatus);
        
        // Update local state to reflect the status change
        setLocalPartialSales(prev => 
          prev.map(listing => 
            listing.id === listingId 
              ? {
                  ...listing,
                  partial_sale_listing_users: listing.partial_sale_listing_users.map(listingUser =>
                    listingUser.id === listingUserId
                      ? { ...listingUser, status: 'interested' }
                      : listingUser
                  )
                }
              : listing
          )
        );
        
        showToastNotification(intl.formatMessage({ id: 'succession.partial_sales.marketplace.toast_approve_success' }), 'success');
      }
    } catch (error) {
      console.error('Error approving interest:', error);
      showToastNotification(intl.formatMessage({ id: 'succession.partial_sales.marketplace.toast_approve_failed' }), 'error');
    }
  };

  const handleRemoveClick = (listing) => {
    setListingToRemove(listing);
    setRemoveDialogOpen(true);
  };

  const handleRemoveConfirm = async () => {
    if (!listingToRemove) return;

    try {
      const userRole = listingToRemove.user.id === user.id ? 'seller' : 'buyer';
      
      await removeListing({
        variables: {
          id: listingToRemove.id,
          userRole: userRole
        }
      });

      // Remove from local state
      setLocalPartialSales(prev => prev.filter(listing => listing.id !== listingToRemove.id));
      
      showToastNotification(intl.formatMessage({ id: 'succession.partial_sales.marketplace.toast_remove_success' }), 'success');
    } catch (error) {
      console.error('Error removing listing:', error);
      if (error.message && error.message.includes('only remove closed listings')) {
        showToastNotification(intl.formatMessage({ id: 'succession.partial_sales.marketplace.toast_remove_only_closed' }), 'error');
      } else {
        showToastNotification(intl.formatMessage({ id: 'succession.partial_sales.marketplace.toast_remove_failed' }), 'error');
      }
    } finally {
      setRemoveDialogOpen(false);
      setListingToRemove(null);
    }
  };

  const handleRemoveCancel = () => {
    setRemoveDialogOpen(false);
    setListingToRemove(null);
  };



  const formatCurrency = (amount) => `${currencySymbol()}${numeral(amount || 0).format('0,0')}`;
  const formatNumber = (number) => numeral(number || 0).format('0,0');

  const getStatusDisplay = (status) => {
    switch(status) {
      case 'new':
        return { label: intl.formatMessage({ id: 'succession.partial_sales.marketplace.status_draft' }), color: 'bg-succession-bg-secondary text-succession-text-secondary border border-succession-border' };
      case 'open':
        return { label: intl.formatMessage({ id: 'succession.partial_sales.marketplace.status_published' }), color: 'bg-succession-success-bg text-succession-success border border-succession-success' };
      case 'closed':
        return { label: intl.formatMessage({ id: 'succession.partial_sales.marketplace.status_closed' }), color: 'bg-succession-error-bg text-succession-error border border-succession-error' };
      default:
        return { label: intl.formatMessage({ id: 'succession.partial_sales.marketplace.status_unknown' }), color: 'bg-succession-bg-secondary text-succession-text-secondary border border-succession-border' };
    }
  };

  const getTotalRevenue = (listing) => {
    const feeBasedRevenue = parseFloat(listing.fee_based_revenue) || 0;
    const trailsRevenue = parseFloat(listing.trails_revenue) || 0;
    const directRevenue = parseFloat(listing.direct_revenue) || 0;
    const commissionRevenue = parseFloat(listing.commission_revenue) || 0;
    
    const total = feeBasedRevenue + trailsRevenue + directRevenue + commissionRevenue;
    return total;
  };

  const getRevenueMultiple = (listing) => {
    const totalRevenue = getTotalRevenue(listing);
    if (totalRevenue === 0) return 0;
    return (listing.asking_price || 0) / totalRevenue;
  };

  // Use local state for calculations and display
  const displayPartialSales = localPartialSales;

  // Calculate the maximum height needed for all cards
  React.useEffect(() => {
    const calculateMaxHeight = () => {
      // Use a timeout to allow DOM to render first
      setTimeout(() => {
        const cardElements = document.querySelectorAll('[data-card-content]');
        let maxHeight = 480; // Minimum height
        
        cardElements.forEach(element => {
          const contentHeight = element.scrollHeight + 48; // Add padding
          if (contentHeight > maxHeight) {
            maxHeight = contentHeight;
          }
        });
        
        // Ensure minimum height and round up to nearest 16px
        const calculatedHeight = Math.max(480, Math.ceil(maxHeight / 16) * 16);
        
        // Only set height once on initial load, don't recalculate during updates
        if (!heightCalculated) {
          setCardHeight(calculatedHeight);
          setHeightCalculated(true);
        }
      }, 100);
    };

    // Only calculate on initial load or when we reset
    if (!heightCalculated) {
      calculateMaxHeight();
    }
    
    // Recalculate on window resize
    const handleResize = () => {
      if (heightCalculated) {
        setHeightCalculated(false); // Allow recalculation on resize
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [displayPartialSales, heightCalculated]);

  const handleFlipCard = (listingId) => {
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(listingId)) {
        newSet.delete(listingId);
        setEditingListings(prev => {
          const newMap = new Map(prev);
          newMap.delete(listingId);
          return newMap;
        });
      } else {
        newSet.add(listingId);
        // Initialize the editing state with current listing data
        const listing = localPartialSales.find(l => l.id === listingId);
        if (listing) {
          setEditingListings(prev => new Map(prev).set(listingId, { ...listing }));
        }
      }
      return newSet;
    });
  };

  const handleEditSave = (listingId, updatedData) => {
    // Update the local state with the new data immediately
    setLocalPartialSales(prevListings => 
      prevListings.map(listing => 
        listing.id === listingId ? { ...listing, ...updatedData } : listing
      )
    );
    
    // Flip back to front of card
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      newSet.delete(listingId);
      return newSet;
    });
    
    setEditingListings(prev => {
      const newMap = new Map(prev);
      newMap.delete(listingId);
      return newMap;
    });
  };

  const handleEditCancel = (listingId) => {
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      newSet.delete(listingId);
      return newSet;
    });
    
    setEditingListings(prev => {
      const newMap = new Map(prev);
      newMap.delete(listingId);
      return newMap;
    });
  };



  const handleViewProfile = (userName) => {
    if (!userName) {
      console.error("Cannot open profile drawer: No username provided");
      return;
    }

    try {
      // Use Redux to open the succession drawer, same as ListCard component
      dispatch(openSuccessionDrawer(userName));
    } catch (error) {
      console.error("Error opening profile drawer:", error);
      // Fallback to URL navigation on error
      window.location.href = `/profiles/${userName}`;
    }
  };

  // Helper functions for page layout
  const getPageTitle = () => {
    return pageTitle || "Partial Sale Marketplace";
  };

  const getPageSubtitle = () => {
    if (isMyPartialSales) {
      return intl.formatMessage({ id: 'succession.partial_sales.marketplace.subtitle_my_sales' });
    }
    return intl.formatMessage({ id: 'succession.partial_sales.marketplace.subtitle_marketplace' });
  };

  const getActionButtons = () => {
    if (isMyPartialSales) {
      return (
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => window.location.href = "/succession/partial_sales"}
            variant="outline"
            size="md"
          >
            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {intl.formatMessage({ id: 'succession.partial_sales.marketplace.back_to_marketplace' })}
          </Button>
          <Button
            onClick={() => window.location.href = "/succession/partial_sales/wizard"}
            variant="primary"
            size="md"
          >
            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {intl.formatMessage({ id: 'succession.partial_sales.marketplace.create_listing' })}
          </Button>
        </div>
      );
    } else {
      return (
        <Button
          onClick={() => window.location.href = "/succession/my_partial_sales"}
          variant="primary"
          size="md"
        >
          <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {intl.formatMessage({ id: 'succession.partial_sales.marketplace.my_partial_sales' })}
        </Button>
      );
    }
  };

  if (!displayPartialSales || displayPartialSales.length === 0) {
    return (
      <SingleColumnTemplate
        title={getPageTitle()}
        subtitle={getPageSubtitle()}
        actions={getActionButtons()}
        variant="wide"
      >
        <div className="text-center py-16">
          <div className="mx-auto w-16 h-16 bg-succession-bg-primary rounded-full flex items-center justify-center mb-4 border border-succession-border">
            <svg className="w-8 h-8 text-succession-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-2 text-succession-text-primary transition-colors duration-300">
            {intl.formatMessage({ id: 'succession.partial_sales.marketplace.no_listings_title' })}
          </h2>
          <p className="text-lg mb-6 text-succession-text-secondary transition-colors duration-300">
            {intl.formatMessage({ id: 'succession.partial_sales.marketplace.no_listings_body' })}
          </p>
          <a
            href="/succession/partial_sales/wizard"
            className="inline-flex items-center px-6 py-3 bg-succession-primary text-white rounded-lg hover:opacity-80 transition-all"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {intl.formatMessage({ id: 'succession.partial_sales.marketplace.create_your_listing' })}
          </a>
        </div>
      </SingleColumnTemplate>
    );
  }

  return (
    <SingleColumnTemplate
      title={getPageTitle()}
      subtitle={getPageSubtitle()}
      actions={getActionButtons()}
      variant="wide"
    >
      {/* Stats - Only show on My Partial Sales page */}
      {isMyPartialSales && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-succession-bg-primary p-4 rounded-lg border border-succession-border transition-colors duration-300">
            <div className="text-2xl font-bold text-succession-primary">{partialSales.length}</div>
            <div className="text-sm text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.stats_total_listings' })}</div>
          </div>
          <div className="bg-succession-bg-primary p-4 rounded-lg border border-succession-border transition-colors duration-300">
            <div className="text-2xl font-bold text-succession-success">
              {partialSales.filter(listing => listing.sale_status === 'open').length}
            </div>
            <div className="text-sm text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.stats_published' })}</div>
          </div>
          <div className="bg-succession-bg-primary p-4 rounded-lg border border-succession-border transition-colors duration-300">
            <div className="text-2xl font-bold text-succession-text-secondary">
              {partialSales.filter(listing => listing.sale_status === 'new').length}
            </div>
            <div className="text-sm text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.stats_drafts' })}</div>
          </div>
          <div className="bg-succession-bg-primary p-4 rounded-lg border border-succession-border transition-colors duration-300">
            <div className="text-2xl font-bold text-succession-accent">
              {formatNumber(partialSales.reduce((sum, listing) => sum + (listing.number_of_clients || 0), 0))}
            </div>
            <div className="text-sm text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.stats_total_clients' })}</div>
          </div>
        </div>
      )}

        {/* Listings Grid */}
        <div className={isMyPartialSales ? "space-y-6" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"}>
          {displayPartialSales.map((listing) => {
            // Check if this listing has interests (only for My Partial Sales)
            const hasInterests = isMyPartialSales && listing.user.id === user.id && listing.partial_sale_listing_users && listing.partial_sale_listing_users.length > 0;
            
            // Check if there are pending interests that need approval
            const hasPendingInterests = hasInterests && listing.partial_sale_listing_users.some(listingUser => 
              !listingUser.status || listingUser.status === null || listingUser.status === 'expressed_interest'
            );

            return (
            <div
              key={listing.id}
              className={`relative w-full ${
                listing.sale_status === 'new' 
                  ? 'border-[var(--succession-border)] border-dashed' 
                  : hasInterests 
                    ? 'border-succession-primary border-2' 
                    : 'border-succession-border'
              }`}
              style={{ 
                perspective: '1000px',
                height: isMyPartialSales ? '600px' : `${cardHeight}px`,
                minHeight: isMyPartialSales ? '600px' : `${cardHeight}px`,
                maxHeight: isMyPartialSales ? '600px' : `${cardHeight}px`,
                ...(hasInterests && {
                  boxShadow: hasPendingInterests 
                    ? '0 0 0 2px var(--succession-primary), 0 0 20px var(--succession-primary), 0 4px 12px rgba(0, 0, 0, 0.1)'
                    : '0 0 0 2px var(--succession-primary-bg), 0 4px 12px rgba(0, 0, 0, 0.1)'
                }),
                ...(hasPendingInterests && {
                  animation: 'pulse-primary 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                })
              }}
            >
              <div
                className={`absolute inset-0 w-full h-full transition-transform duration-700 transform-style-preserve-3d ${
                  flippedCards.has(listing.id) ? 'rotate-y-180' : ''
                }`}
              >
                {/* Front of Card */}
                <div className="absolute inset-0 w-full h-full backface-hidden bg-succession-bg-primary rounded-lg border shadow-sm hover:shadow-md transition-all duration-300">
                  <div className="p-6 flex flex-col h-full" data-card-content>
                    <div className="flex-1 overflow-y-auto">
                {/* Header */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-1 text-succession-text-primary transition-colors duration-300">
                      {listing.segment_details || 'Practice Segment'}
                    </h3>
                    <div className="flex items-start text-sm text-succession-text-secondary transition-colors duration-300">
                      <svg className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="break-words">
                        {listing.states?.join(', ') || 'Location not specified'}
                      </span>
                    </div>
                    {/* Seller's Transition Goals */}
                    {listing.user?.transition_goal_names && listing.user.transition_goal_names.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {listing.user.transition_goal_names.map((goal, index) => (
                          <span
                            key={index}
                            className="inline-flex px-2 py-0.5 text-xs rounded-full bg-succession-primary-bg text-succession-primary border border-succession-primary transition-colors duration-300"
                          >
                            {goal}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.status_label' })}</div>
                    <div className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusDisplay(listing.sale_status).color}`}>
                      {getStatusDisplay(listing.sale_status).label}
                    </div>
                  </div>
                </div>

                {/* Views and Interests - Only show on My Partial Sales */}
                {isMyPartialSales && (
                  <div className="mb-4 space-y-2">
                    <div className="flex justify-between items-center px-3 py-2 bg-succession-bg-secondary rounded-lg transition-colors duration-300">
                      <div className="flex items-center text-sm text-succession-text-secondary">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <span className="font-medium text-succession-text-primary">{listing.views_count || 0}</span>
                        <span className="ml-1">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.views' })}</span>
                      </div>
                      <div className="flex items-center text-sm text-succession-text-secondary">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                        <span className="font-medium text-succession-text-primary">{listing.interests_count || 0}</span>
                        <span className="ml-1">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.interests' })}</span>
                      </div>
                    </div>
                    {(listing.listed_at || listing.created_at) && (
                      <div className="flex items-center text-sm text-succession-text-secondary px-3 py-2 bg-succession-bg-secondary rounded-lg transition-colors duration-300">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="mr-1">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.listed_label' })}</span>
                        <span className="font-medium text-succession-text-primary">
                          {listing.listed_at ? new Date(listing.listed_at).toLocaleDateString() : new Date(listing.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="text-sm text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.clients_label' })}</div>
                    <div className="text-lg font-semibold text-succession-text-primary transition-colors duration-300">
                      {formatNumber(listing.number_of_clients)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.revenue_label' })}</div>
                    <div className="text-lg font-semibold text-succession-success">
                      {formatCurrency(getTotalRevenue(listing))}
                    </div>
                  </div>
                  {!hideProductSalesPurchasePrice && (
                    <div>
                      <div className="text-sm text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.asking_price_label' })}</div>
                      <div className="text-lg font-semibold text-succession-primary">
                        {(() => {
                          return !listing.asking_price || listing.asking_price === 0 || listing.asking_price === '0' || parseFloat(listing.asking_price) === 0 ? intl.formatMessage({ id: 'succession.partial_sales.marketplace.negotiable' }) : formatCurrency(listing.asking_price);
                        })()}
                      </div>
                    </div>
                  )}
                </div>

                {/* Description */}
                {listing.description && (
                  <div className="mb-4">
                    <p className="text-sm text-succession-text-secondary transition-colors duration-300 leading-relaxed">
                      {listing.description}
                    </p>
                  </div>
                )}

                {/* Revenue Breakdown */}
                <div className="mb-4">
                  <div className="text-sm font-medium mb-2 text-succession-text-primary transition-colors duration-300">
                    {intl.formatMessage({ id: 'succession.partial_sales.marketplace.revenue_breakdown_label' })}
                  </div>
                  <div className="space-y-1 text-xs">
                    {listing.fee_based_revenue > 0 && (
                      <div className="flex justify-between">
                        <span className="text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.fee_based' })}</span>
                        <span className="text-succession-text-primary transition-colors duration-300">{formatCurrency(listing.fee_based_revenue)}</span>
                      </div>
                    )}
                    {listing.trails_revenue > 0 && (
                      <div className="flex justify-between">
                        <span className="text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.trails' })}</span>
                        <span className="text-succession-text-primary transition-colors duration-300">{formatCurrency(listing.trails_revenue)}</span>
                      </div>
                    )}
                    {listing.direct_revenue > 0 && (
                      <div className="flex justify-between">
                        <span className="text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.direct' })}</span>
                        <span className="text-succession-text-primary transition-colors duration-300">{formatCurrency(listing.direct_revenue)}</span>
                      </div>
                    )}
                    {listing.commission_revenue > 0 && (
                      <div className="flex justify-between">
                        <span className="text-succession-text-secondary transition-colors duration-300">
                          {partialSalesPropertyCasualty ? intl.formatMessage({ id: 'succession.partial_sales.marketplace.property_casualty' }) : intl.formatMessage({ id: 'succession.partial_sales.marketplace.commission' })}
                        </span>
                        <span className="text-succession-text-primary transition-colors duration-300">{formatCurrency(listing.commission_revenue)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* More Details */}
                {partialSalesDetailedQuestions && (listing.client_demographics_details || listing.services_provided_details || listing.other_details) && (
                  <div>
                    <h4 className="font-medium text-succession-text-primary mb-2 transition-colors duration-300">
                      {intl.formatMessage({ id: 'succession.partial_sales.marketplace.more_details' })}
                    </h4>
                    <div className="bg-succession-bg-secondary rounded-lg p-3 text-sm max-h-40 overflow-y-auto transition-colors duration-300 space-y-2">
                      {listing.client_demographics_details && (
                        <div>
                          <span className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.client_demographics_label' })}</span>
                          <p className="text-succession-text-primary">{listing.client_demographics_details}</p>
                        </div>
                      )}
                      {listing.services_provided_details && (
                        <div>
                          <span className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.services_provided_label' })}</span>
                          <p className="text-succession-text-primary">{listing.services_provided_details}</p>
                        </div>
                      )}
                      {listing.other_details && (
                        <div>
                          <span className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.other_label' })}</span>
                          <p className="text-succession-text-primary">{listing.other_details}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Interests - Only show on My Partial Sales for own listings with interests */}
                {isMyPartialSales && listing.user.id === user.id && listing.partial_sale_listing_users && listing.partial_sale_listing_users.length > 0 && (
                  <div>
                    <h4 className="font-medium text-succession-text-primary mb-2 transition-colors duration-300 flex items-center">
                      {intl.formatMessage({ id: 'succession.partial_sales.marketplace.interests_section' }, { count: listing.partial_sale_listing_users.length })}
                      {hasPendingInterests && (
                        <span className="ml-2 flex items-center">
                          <span className="relative flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-succession-primary opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-succession-primary"></span>
                          </span>
                          <span className="ml-1 text-xs text-succession-primary font-medium">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.new_badge' })}</span>
                        </span>
                      )}
                    </h4>
                    <div className="bg-succession-bg-secondary rounded-lg p-3 text-sm max-h-48 overflow-y-auto transition-colors duration-300 space-y-3">
                      {listing.partial_sale_listing_users.map((listingUser, index) => {
                        const isPending = !listingUser.status || listingUser.status === null || listingUser.status === 'expressed_interest';
                        return (
                        <div key={listingUser.id || index} className={`flex items-center justify-between p-2 bg-succession-bg-primary rounded border transition-all duration-300 ${
                          isPending ? 'border-succession-primary bg-succession-primary-bg' : 'border-succession-border'
                        }`}>
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <div>
                                <Button
                                  as="button"
                                  onClick={() => handleViewProfile(listingUser.user.fb_user_name || listingUser.user.user_name)}
                                  variant="ghost"
                                  size="sm"
                                  className="font-medium text-succession-primary hover:text-succession-primary-hover underline p-0 h-auto"
                                >
                                  {listingUser.user.allowed_name || listingUser.user.name || 'Anonymous'}
                                </Button>  
                                <p className="text-xs text-succession-text-secondary">
                                  {listingUser.user.city && listingUser.user.province ? 
                                    `${listingUser.user.city}, ${listingUser.user.province}` : 
                                    'Location not specified'
                                  }
                                </p>
                                {listingUser.user.assets_under_management && (
                                  <p className="text-xs text-succession-text-secondary">
                                    {currencySymbol()}{numeral(listingUser.user.assets_under_management).format('0,0')} AUM
                                  </p>
                                )}
                              </div>
                            </div>
                            {listingUser.request_message && (
                              <div className="mt-2 p-2 bg-succession-bg-secondary rounded text-xs">
                                <span className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.interest_message_label' })}</span>
                                <p className="text-succession-text-primary mt-1">{listingUser.request_message}</p>
                              </div>
                            )}
                          </div>
                          <div className="ml-3">
                            {/* Show approve button for new interests, message button for approved */}
                            {(!listingUser.status || listingUser.status === null || listingUser.status === 'expressed_interest') ? (
                              <Button
                                onClick={() => handleApproveInterest(listingUser.id, listing.id)}
                                variant="primary"
                                size="sm"
                                className="bg-succession-success hover:bg-succession-success/90 text-xs"
                              >
                                {intl.formatMessage({ id: 'succession.partial_sales.marketplace.approve_button' })}
                              </Button>
                            ) : listingUser.status === 'interested' ? (
                              <Button
                                onClick={() => {
                                // Create and submit form dynamically
                                const form = document.createElement('form');
                                form.method = 'POST';
                                form.action = '/messages/create_chat_from_partial_sale_user';
                                
                                // Add friendly_id
                                const friendlyIdInput = document.createElement('input');
                                friendlyIdInput.type = 'hidden';
                                friendlyIdInput.name = 'friendly_id';
                                friendlyIdInput.value = listingUser.friendly_name;
                                form.appendChild(friendlyIdInput);
                                
                                // Add authenticity token
                                const tokenInput = document.createElement('input');
                                tokenInput.type = 'hidden';
                                tokenInput.name = 'authenticity_token';
                                tokenInput.value = window.ReactOnRails?.authenticityToken() || '';
                                form.appendChild(tokenInput);
                                
                                // Add to document and submit
                                document.body.appendChild(form);
                                form.submit();
                                document.body.removeChild(form);
                              }}
                              variant="primary"
                              size="sm"
                              className="text-xs"
                            >
                              {intl.formatMessage({ id: 'succession.partial_sales.marketplace.message_button' })}
                            </Button>
                            ) : (
                              <span className="px-3 py-1 bg-succession-text-muted text-white text-xs rounded">
                                {intl.formatMessage({ id: 'succession.partial_sales.marketplace.pending_badge' })}
                              </span>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

                    {/* Action Button - Always at bottom */}
                    <div className="mt-4 pt-4 border-t border-succession-border">
                    {listing.user.id === user.id ? (
                      // Own listing - show edit/manage actions
                      <>
                        <div className="flex space-x-2">
                          <Button
                            onClick={() => handleFlipCard(listing.id)}
                            variant="primary"
                            size="md"
                            className="flex-1"
                          >
                            {intl.formatMessage({ id: 'succession.partial_sales.marketplace.edit_button' })}
                          </Button>
                          {listing.sale_status === 'new' && (
                            <Button
                              onClick={() => handlePublishListing(listing)}
                              variant="primary"
                              size="md"
                              className="flex-1 bg-succession-success hover:bg-succession-success/90"
                            >
                              {intl.formatMessage({ id: 'succession.partial_sales.marketplace.publish_button' })}
                            </Button>
                          )}
                        </div>
                        <Button
                          onClick={() => handleRemoveClick(listing)}
                          variant="primary"
                          size="md"
                          className="w-full mt-2 bg-succession-error hover:opacity-90"
                        >
                          {intl.formatMessage({ id: 'succession.partial_sales.marketplace.remove_button' })}
                        </Button>
                      </>
                    ) : listing.sale_status === 'new' ? (
                      // Draft listing from another user - disabled
                      <Button
                        disabled
                        variant="outline"
                        size="md"
                        className="w-full"
                      >
                        {intl.formatMessage({ id: 'succession.partial_sales.marketplace.draft_not_available' })}
                      </Button>
                    ) : listing.current_user_is_an_interest ? (
                      // User has already expressed interest - show disabled state
                      <Button
                        disabled
                        variant="outline"
                        size="md"
                        className="w-full"
                      >
                        {intl.formatMessage({ id: 'succession.partial_sales.marketplace.interest_already_expressed' })}
                      </Button>
                    ) : listing.sale_status === 'closed' ? (
                      // Closed listing from another user - allow buyer to remove
                      <Button
                        onClick={() => handleRemoveClick(listing)}
                        variant="primary"
                        size="md"
                        className="w-full bg-succession-error hover:opacity-90"
                      >
                        Remove
                      </Button>
                    ) : (
                      // Published listing from another user - express interest
                      <Button
                        onClick={() => handleExpressInterest(listing)}
                        variant="primary"
                        size="md"
                        className="w-full"
                      >
                        {intl.formatMessage({ id: 'succession.partial_sales.marketplace.express_interest' })}
                      </Button>
                    )}
                    </div>
                  </div>
                </div>

                {/* Back of Card - Edit Form */}
                <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 bg-succession-bg-primary rounded-lg border shadow-sm overflow-hidden">
                  <div className="h-full overflow-y-auto">
                    {flippedCards.has(listing.id) && editingListings.has(listing.id) && (
                      <PartialSaleEditForm
                        listing={editingListings.get(listing.id)}
                        onCancel={() => handleEditCancel(listing.id)}
                        onSave={(updatedData) => handleEditSave(listing.id, updatedData)}
                        company={company}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            );
          })}
          </div>

        {/* Interest Modal */}
        <Modal
          open={isInterestModalOpen && !!selectedListing}
          onClose={() => {
            setIsInterestModalOpen(false);
            setSelectedListing(null);
            setInterestMessage('');
          }}
        >
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4 text-succession-text-primary transition-colors duration-300">
              {intl.formatMessage({ id: 'succession.partial_sales.marketplace.interest_modal_title' })}
            </h2>
            <p className="text-sm mb-4 text-succession-text-secondary transition-colors duration-300">
              {intl.formatMessage({ id: 'succession.partial_sales.marketplace.interest_modal_desc' }, { segment: selectedListing?.segment_details || '' })}
            </p>
            <textarea
              value={interestMessage}
              onChange={(e) => setInterestMessage(e.target.value)}
              className="w-full px-3 py-2 border border-succession-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)] focus:border-transparent bg-succession-bg-secondary text-succession-text-primary transition-colors duration-300"
              rows={4}
              placeholder={intl.formatMessage({ id: 'succession.partial_sales.marketplace.interest_modal_placeholder' })}
            />
            <div className="flex justify-end space-x-3 mt-4">
              <Button
                onClick={() => {
                  setIsInterestModalOpen(false);
                  setSelectedListing(null);
                  setInterestMessage('');
                }}
                variant="outline"
                size="md"
              >
                {intl.formatMessage({ id: 'succession.partial_sales.marketplace.interest_modal_cancel' })}
              </Button>
              <Button
                onClick={handleSubmitInterest}
                disabled={isSubmittingInterest || !interestMessage.trim()}
                variant="primary"
                size="md"
              >
                {isSubmittingInterest ? (
                  <div className="flex items-center">
                    <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" className="opacity-25" />
                      <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {intl.formatMessage({ id: 'succession.partial_sales.marketplace.interest_modal_sending' })}
                  </div>
                ) : (
                  intl.formatMessage({ id: 'succession.partial_sales.marketplace.interest_modal_send' })
                )}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Toast Notification */}
        {showToast && (
          <div className={`fixed top-4 right-4 z-50 max-w-sm w-full transform transition-all duration-300 ease-in-out ${
            showToast ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
          }`}>
            <div className={`rounded-lg p-4 shadow-lg border-l-4 ${
              toastType === 'success' 
                ? 'bg-succession-success-bg border-succession-success text-succession-success' 
                : toastType === 'error'
                ? 'bg-succession-error-bg border-succession-error text-succession-error'
                : 'bg-succession-primary-20 border-succession-primary text-succession-primary'
            }`}>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  {toastType === 'success' ? (
                    <svg className="w-5 h-5 text-succession-success" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : toastType === 'error' ? (
                    <svg className="w-5 h-5 text-succession-error" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-succession-primary" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium">{toastMessage}</p>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <Button
                    as="button"
                    onClick={() => setShowToast(false)}
                    variant="ghost"
                    size="sm"
                    className={`p-0 h-auto ${
                      toastType === 'success' 
                        ? 'text-succession-success hover:opacity-80' 
                        : toastType === 'error'
                        ? 'text-succession-error hover:opacity-80'
                        : 'text-succession-primary hover:opacity-80'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Remove Confirmation Dialog */}
        <Modal
          open={removeDialogOpen}
          onClose={handleRemoveCancel}
          className="bg-succession-bg-primary text-succession-text-primary rounded-lg shadow-succession-lg border border-succession-border w-full max-w-md"
        >
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4 text-succession-text-primary transition-colors duration-300">
              {intl.formatMessage({ id: 'succession.partial_sales.marketplace.remove_modal_title' })}
            </h2>
            <p className="text-sm mb-6 text-succession-text-secondary transition-colors duration-300">
              {intl.formatMessage({ id: 'succession.partial_sales.marketplace.remove_modal_desc' })}
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                onClick={handleRemoveCancel}
                variant="outline"
                size="md"
              >
                {intl.formatMessage({ id: 'succession.partial_sales.marketplace.remove_modal_cancel' })}
              </Button>
              <Button
                onClick={handleRemoveConfirm}
                variant="primary"
                size="md"
                className="bg-succession-error hover:opacity-90"
              >
                {intl.formatMessage({ id: 'succession.partial_sales.marketplace.remove_modal_confirm' })}
              </Button>
            </div>
          </div>
        </Modal>
    </SingleColumnTemplate>
  );
};

export default SuccessionPartialSalesMarketplace;